import type { ReactNode } from "react";

import {
  PlusIcon,
  SlidersHorizontalIcon,
  TrendUpIcon,
} from "@phosphor-icons/react";
import {
  Button,
  DropdownMenu,
  Flex,
  IconButton,
  Popover,
  Tooltip,
} from "@radix-ui/themes";

import { WIDGET_KIND_OPTIONS, type WidgetKind } from "./widgetKind";

export interface LayoutToolbarPresentationProps {
  onAddWidget: (kind: WidgetKind) => void;
  /**
   * Toggles the order-ticket drawer. Omit to hide the Trade control entirely
   * (engines that do not wire a drawer). Pair with `orderTicketOpen` so the
   * button can show its pressed state.
   */
  onToggleOrderTicket?: () => void;
  /** Whether the order-ticket drawer is currently open. */
  orderTicketOpen?: boolean;
  disabled?: boolean;
  settings?: {
    expanded: boolean;
    buttonId: string;
    panelId: string;
    onToggle: () => void;
    /** Settings panel rendered inside the popover. */
    panel?: ReactNode;
  };
}

const ICON_SIZE = 16;

/**
 * Workspace toolbar, rendered into the tab strip via `WorkspaceToolbarPortal`.
 *
 * Add widget is the one call to action here, so it is a labelled Radix
 * `Button` with a menu chevron; its visible text is its accessible name, which
 * is why it carries no `aria-label` or `Tooltip`. It takes the accent on the
 * `soft` variant — a tinted chip rather than a filled one, so the toolbar
 * reads as chrome next to the board. Trade (when wired) and Layout settings
 * sit beside it as neutral icon-only `IconButton`s named by `aria-label` and
 * explained by a `Tooltip`.
 *
 * Add widget is a Radix `DropdownMenu` and Layout settings a Radix `Popover`;
 * Radix owns both portals and their theme propagation, so no local portal root
 * is needed. Layouts are always editable.
 */
export const LayoutToolbarPresentation = ({
  onAddWidget,
  onToggleOrderTicket,
  orderTicketOpen = false,
  disabled = false,
  settings,
}: LayoutToolbarPresentationProps) => {
  return (
    <Flex align="center" gap="2">
      {onToggleOrderTicket ? (
        <Tooltip content="Trade">
          {/* A toggle, not a launcher: `aria-pressed` announces whether the
              drawer is open, and the accent `soft` variant gives the same
              open-state affordance the Layout settings popover trigger has. */}
          <IconButton
            type="button"
            variant={orderTicketOpen ? "soft" : "surface"}
            color={orderTicketOpen ? undefined : "gray"}
            aria-label="Trade"
            aria-pressed={orderTicketOpen}
            onClick={onToggleOrderTicket}
          >
            <TrendUpIcon size={ICON_SIZE} aria-hidden />
          </IconButton>
        </Tooltip>
      ) : null}

      <DropdownMenu.Root>
        {/* `disabled` lives on the child <Button>, not on the Trigger — Radix
            forwards accessibility state from the button, and duplicating it on
            the Trigger would fight React's DOM-attribute dedup and make the
            disabled contract ambiguous. */}
        <DropdownMenu.Trigger>
          <Button type="button" variant="soft" disabled={disabled}>
            <PlusIcon size={ICON_SIZE} aria-hidden />
            Add widget
            <DropdownMenu.TriggerIcon />
          </Button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content align="end" size="1" variant="soft">
          {WIDGET_KIND_OPTIONS.map((option) => (
            <DropdownMenu.Item
              key={option.kind}
              onSelect={() => onAddWidget(option.kind)}
            >
              {option.label}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Root>

      {settings ? (
        // Controlled by the tab's settings controller, which still owns
        // `expanded`. Radix only fires onOpenChange on an actual change, so
        // forwarding it as a toggle keeps the two in step — including the
        // outside-click and Escape paths the old inline disclosure never had.
        <Popover.Root
          open={settings.expanded}
          onOpenChange={() => settings.onToggle()}
        >
          <Tooltip content="Layout settings">
            <Popover.Trigger>
              <IconButton
                id={settings.buttonId}
                type="button"
                variant="surface"
                color="gray"
                aria-label="Layout settings"
                aria-controls={settings.panelId}
                disabled={disabled}
              >
                <SlidersHorizontalIcon size={ICON_SIZE} aria-hidden />
              </IconButton>
            </Popover.Trigger>
          </Tooltip>
          <Popover.Content
            align="end"
            size="1"
            width="640px"
            maxWidth="90vw"
            maxHeight="70vh"
            className="overflow-y-auto"
          >
            {settings.panel}
          </Popover.Content>
        </Popover.Root>
      ) : null}
    </Flex>
  );
};
